/** G1 workstation load: data hook and pure drill-down grouping for You › Machines. */
import { useQuery } from '@tanstack/react-query'
import {
  type LoadBucket,
  type LoadResource,
  loadResourceName,
  type MachineLoadFrame,
  recommendCapacity,
} from '@viko/protocol'
import { useBackend } from './backend.js'
import { useFallbackPoll } from './queries.js'

/**
 * Under the machines key: each `machine.load` report put reaches us as the hub's
 * `work{machine}` push, which invalidates `['machines', …]` (no interval polling).
 */
export function useMachineLoad() {
  const b = useBackend()
  const refetchInterval = useFallbackPoll(5 * 60_000)
  return useQuery({
    queryKey: ['machines', 'load', b.artifactAccess.accountId] as const,
    queryFn: () => b.api.machineLoad(),
    retry: false,
    refetchInterval,
  })
}

export interface LoadGroup {
  /** Group start (ms). */
  at: number
  buckets: LoadBucket[]
  cpuAvg: number
  cpuMax: number
  memAvg: number
  memMax: number
  gpuMax?: number
  /** Percent of the group's samples that were over a threshold. */
  overPct: number
  overBy: LoadResource[]
}

/** Group buckets by local day (`'day'`) or local hour (`'hour'`), newest first. */
export function groupBuckets(
  history: readonly LoadBucket[],
  by: 'day' | 'hour',
  zone?: number,
): LoadGroup[] {
  const key = (t: number) => {
    const d = new Date(t - (zone ?? 0))
    return by === 'day'
      ? Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
      : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours())
  }
  const map = new Map<number, LoadBucket[]>()
  for (const b of history) map.set(key(b.at), [...(map.get(key(b.at)) ?? []), b])
  return [...map.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([k, buckets]) => {
      const n = buckets.reduce((s, b) => s + b.n, 0)
      const wavg = (f: (b: LoadBucket) => number) =>
        Math.round((buckets.reduce((s, b) => s + f(b) * b.n, 0) / n) * 10) / 10
      const gpu = buckets.flatMap((b) => (b.gpuMax === undefined ? [] : [b.gpuMax]))
      return {
        at: k + (zone ?? 0),
        buckets,
        cpuAvg: wavg((b) => b.cpuAvg),
        cpuMax: Math.max(...buckets.map((b) => b.cpuMax)),
        memAvg: wavg((b) => b.memAvg),
        memMax: Math.max(...buckets.map((b) => b.memMax)),
        ...(gpu.length ? { gpuMax: Math.max(...gpu) } : {}),
        overPct: Math.round((100 * buckets.reduce((s, b) => s + b.over, 0)) / n),
        overBy: [...new Set(buckets.flatMap((b) => b.overBy))],
      }
    })
}

export const overLabel = (r: readonly LoadResource[]) =>
  r.length ? r.map(loadResourceName).join(', ') : 'none'

/**
 * Recommendation for one machine, re-derived here (not just read from the report) because only
 * the browser sees every machine: a peer with headroom turns "upgrade" into "rebalance".
 */
export function recommendationsFor(frame: MachineLoadFrame, peers: readonly MachineLoadFrame[]) {
  const peersHaveHeadroom = peers.some(
    (p) => !p.overloaded && p.sample.cpuPct < 60 && p.sample.memPct < 75,
  )
  return recommendCapacity(frame.history, frame.thresholds, { peersHaveHeadroom })
}
